import React, { useState } from "react";
import { useAuth } from "@/auth";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Eye, EyeOff, Leaf } from "lucide-react";

export default function LoginPage() {
  const { login, signup, user } = useAuth();
  const nav = useNavigate();

  const [tab, setTab] = useState("signin");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ name: "", job_title: "", email: "", password: "" });
  const [pending, setPending] = useState(false);
  const [approvalMsg, setApprovalMsg] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  React.useEffect(() => { if (user && user.id) nav("/"); }, [user, nav]);

  const doLogin = async (e) => {
    e.preventDefault();
    setPending(true);
    try {
      await login(loginForm.email, loginForm.password);
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setPending(false); }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    setPending(true);
    try {
      const res = await signup({ ...signupForm, requested_role: "sales" });
      setApprovalMsg(res.message || "Your account is pending admin approval.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setPending(false); }
  };

  const labelCls = "block text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5";
  const inputCls = "h-11 rounded-xl bg-white";

  return (
    <div className="min-h-screen bg-white flex flex-col items-center px-4 py-12">
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
        <Leaf className="w-7 h-7 text-green-600" />
      </div>
      <h1 className="text-2xl font-semibold text-slate-900 mt-5 text-center">Freshcare Business Solutions</h1>
      <p className="text-sm text-slate-500 mt-1.5 text-center">Internal toolkit for the container sales team</p>

      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-sm p-6 mt-8">
        <div className="grid grid-cols-2 bg-slate-100 rounded-full p-1 mb-6">
          <button onClick={() => setTab("signin")} data-testid="auth-signin-tab"
            className={`h-9 rounded-full text-sm font-medium transition-colors ${tab === "signin" ? "bg-blue-600 text-white" : "text-slate-600"}`}>
            Sign in
          </button>
          <button onClick={() => setTab("register")} data-testid="auth-signup-tab"
            className={`h-9 rounded-full text-sm font-medium transition-colors ${tab === "register" ? "bg-blue-600 text-white" : "text-slate-600"}`}>
            Register
          </button>
        </div>

        {tab === "signin" ? (
          <form onSubmit={doLogin} className="space-y-4">
            <div>
              <label className={labelCls}>Work Email</label>
              <Input type="email" required value={loginForm.email} className={inputCls}
                onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} data-testid="login-email-input" />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <div className="relative">
                <Input type={showLoginPassword ? "text" : "password"} required value={loginForm.password} className={`${inputCls} pr-11`}
                  onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} data-testid="login-password-input" />
                <button type="button" onClick={() => setShowLoginPassword(v => !v)}
                  aria-label={showLoginPassword ? "Hide password" : "Show password"}
                  title={showLoginPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-slate-500 hover:text-slate-700"
                  data-testid="login-password-toggle">
                  {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={pending} data-testid="login-submit-button"
              className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors disabled:opacity-60">
              {pending ? "Signing in…" : "Sign in"}
            </button>
          </form>
        ) : approvalMsg ? (
          <div data-testid="auth-pending-approval-banner" className="p-4 rounded-xl border border-amber-300 bg-amber-50">
            <div className="text-sm font-semibold text-amber-900">Pending administrator approval</div>
            <p className="text-xs text-amber-800 mt-1.5">{approvalMsg}</p>
            <p className="text-xs text-amber-700 mt-2">Your admin will assign your role and enable access. You can sign in after approval.</p>
          </div>
        ) : (
          <form onSubmit={doSignup} className="space-y-4">
            <div>
              <label className={labelCls}>Full Name</label>
              <Input required value={signupForm.name} className={inputCls}
                onChange={e => setSignupForm({ ...signupForm, name: e.target.value })} data-testid="signup-name-input" />
            </div>
            <div>
              <label className={labelCls}>Job Title</label>
              <Input value={signupForm.job_title} placeholder="Sales Executive" className={inputCls}
                onChange={e => setSignupForm({ ...signupForm, job_title: e.target.value })} data-testid="signup-jobtitle-input" />
            </div>
            <div>
              <label className={labelCls}>Work Email</label>
              <Input type="email" required value={signupForm.email} className={inputCls}
                onChange={e => setSignupForm({ ...signupForm, email: e.target.value })} data-testid="signup-email-input" />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <div className="relative">
                <Input type={showSignupPassword ? "text" : "password"} required minLength={6} value={signupForm.password} className={`${inputCls} pr-11`}
                  onChange={e => setSignupForm({ ...signupForm, password: e.target.value })} data-testid="signup-password-input" />
                <button type="button" onClick={() => setShowSignupPassword(v => !v)}
                  aria-label={showSignupPassword ? "Hide password" : "Show password"}
                  title={showSignupPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-slate-500 hover:text-slate-700"
                  data-testid="signup-password-toggle">
                  {showSignupPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={pending} data-testid="signup-submit-button"
              className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors disabled:opacity-60">
              {pending ? "Creating…" : "Create account"}
            </button>
          </form>
        )}

        <div className="text-center mt-4">
          <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline" data-testid="forgot-password-link">
            Forgot password?
          </Link>
        </div>

        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-400">or</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <button type="button" onClick={() => toast.info("Google sign-in is not enabled here — use your work email and password.")}
          data-testid="google-signin-button"
          className="w-full h-11 rounded-xl border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
          Continue with Google
        </button>
      </div>

      <p className="text-xs text-slate-400 mt-6 text-center">Authorized staff only. Contact your manager for access.</p>
    </div>
  );
}
